import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Download,
  FileText,
  CheckCircle2,
  AlertCircle,
  Globe,
  Sparkles,
  Database,
  Zap,
  Map
} from 'lucide-react';
import {
  generateCompleteSitemap,
  generateSitemapXML,
  generateSitemapIndex,
  splitIntoMultipleSitemaps,
  calculateSitemapStats,
  validateSitemap,
  generateRobotsTxt,
  SitemapURL
} from '@/utils/seo/sitemapGenerator';

const SitemapGeneratorDashboard: React.FC = () => {
  const [sitemap, setSitemap] = useState<SitemapURL[]>([]);
  const [sitemapXML, setSitemapXML] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [validationResult, setValidationResult] = useState<{
    valid: boolean;
    errors: string[];
    warnings: string[];
  } | null>(null);

  // Calculate stats from database
  const [stats, setStats] = React.useState({
    totalURLs: 0,
    cityLandings: 0,
    specialtyLandings: 0,
    staticPages: 0,
    sitemapSize: 0,
    estimatedIndexablePages: 0
  });
  
  React.useEffect(() => {
    calculateSitemapStats().then(setStats);
  }, []);

  const generateSitemap = async () => {
    setIsGenerating(true);
    setProgress(0);

    // Simulate progress for UX
    const progressInterval = setInterval(() => {
      setProgress(prev => Math.min(prev + 10, 90));
    }, 200);

    try {
      console.log('🚀 Starting sitemap generation with database translations...');
      
      // Generate complete sitemap (now async)
      const urls = await generateCompleteSitemap();
      setSitemap(urls);

      // Validate
      const validation = validateSitemap(urls);
      setValidationResult(validation);

      // Generate XML
      const xml = generateSitemapXML(urls);
      setSitemapXML(xml);

      setProgress(100);
      toast.success(`✅ Sitemap generated with ${urls.length.toLocaleString()} URLs!`);
    } catch (error) {
      console.error('❌ Error generating sitemap:', error);
      toast.error('Failed to generate sitemap. Please check console for details.');
    } finally {
      clearInterval(progressInterval);
      setIsGenerating(false);
    }
  };

  const downloadSitemap = () => {
    const blob = new Blob([sitemapXML], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sitemap.xml';
    link.click();
  };

  const downloadSitemapIndex = () => {
    const chunks = splitIntoMultipleSitemaps(sitemap);
    const today = new Date().toISOString().split('T')[0];
    
    const sitemaps = chunks.map(chunk => ({
      filename: chunk.filename,
      lastmod: today,
      urlCount: chunk.urls.length
    }));

    const indexXML = generateSitemapIndex(sitemaps);
    const blob = new Blob([indexXML], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sitemap-index.xml';
    link.click();
  };

  const downloadRobotsTxt = () => {
    const robotsTxt = generateRobotsTxt();
    const blob = new Blob([robotsTxt], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'robots.txt';
    link.click();
  };

  const downloadAllSitemaps = () => {
    const chunks = splitIntoMultipleSitemaps(sitemap);
    
    chunks.forEach(chunk => {
      const xml = generateSitemapXML(chunk.urls);
      const blob = new Blob([xml], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = chunk.filename;
      link.click();
    });
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2 flex items-center gap-2">
          <Map className="h-8 w-8" />
          Sitemap Generator Dashboard
        </h1>
        <p className="text-muted-foreground">
          Generate comprehensive multilingual sitemap with ~{stats.totalURLs.toLocaleString()} URLs from database translations
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total URLs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.totalURLs.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Across all landing pages
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              City Landings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.cityLandings.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Main city pages
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Specialty Pages
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.specialtyLandings.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Specialty sub-landings
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Sitemap Size
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {(stats.sitemapSize / 1024 / 1024).toFixed(1)} MB
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Estimated total size
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Generation Controls */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Generate Sitemap</CardTitle>
          <CardDescription>
            Create optimized sitemap.xml with images and metadata
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Button
              onClick={generateSitemap}
              disabled={isGenerating}
              className="gap-2"
              size="lg"
            >
              {isGenerating ? (
                <>
                  <Zap className="h-4 w-4 animate-pulse" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  Generate Complete Sitemap
                </>
              )}
            </Button>

            {isGenerating && (
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span>Generation Progress</span>
                  <span>{progress}%</span>
                </div>
                <Progress value={progress} />
              </div>
            )}

            {sitemap.length > 0 && !isGenerating && (
              <div className="flex flex-wrap gap-3 pt-4 border-t">
                <Button onClick={downloadSitemap} variant="outline" className="gap-2">
                  <Download className="h-4 w-4" />
                  Download sitemap.xml
                </Button>

                <Button onClick={downloadSitemapIndex} variant="outline" className="gap-2">
                  <Download className="h-4 w-4" />
                  Download sitemap-index.xml
                </Button>

                <Button onClick={downloadAllSitemaps} variant="outline" className="gap-2">
                  <Download className="h-4 w-4" />
                  Download All Sitemaps
                </Button>

                <Button onClick={downloadRobotsTxt} variant="outline" className="gap-2">
                  <FileText className="h-4 w-4" />
                  Download robots.txt
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Validation Results */}
      {validationResult && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {validationResult.valid ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-600" />
              )}
              Validation Results
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {validationResult.valid ? (
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertTitle>Sitemap is valid!</AlertTitle>
                <AlertDescription>
                  All {sitemap.length.toLocaleString()} URLs pass validation checks and comply with sitemap protocol standards.
                </AlertDescription>
              </Alert>
            ) : (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Validation Errors Found</AlertTitle>
                <AlertDescription>
                  {validationResult.errors.length} error(s) detected. Please review below.
                </AlertDescription>
              </Alert>
            )}

            {validationResult.errors.length > 0 && (
              <div>
                <h3 className="font-semibold text-red-600 mb-2">Errors:</h3>
                <ul className="list-disc list-inside space-y-1">
                  {validationResult.errors.map((error, idx) => (
                    <li key={idx} className="text-sm text-red-600">{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {validationResult.warnings.length > 0 && (
              <div>
                <h3 className="font-semibold text-yellow-600 mb-2">Warnings:</h3>
                <ul className="list-disc list-inside space-y-1">
                  {validationResult.warnings.map((warning, idx) => (
                    <li key={idx} className="text-sm text-yellow-600">{warning}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Sitemap Preview */}
      {sitemapXML && (
        <Tabs defaultValue="preview" className="space-y-6">
          <TabsList>
            <TabsTrigger value="preview">Sitemap Preview</TabsTrigger>
            <TabsTrigger value="stats">Statistics</TabsTrigger>
            <TabsTrigger value="structure">URL Structure</TabsTrigger>
          </TabsList>

          <TabsContent value="preview">
            <Card>
              <CardHeader>
                <CardTitle>Sitemap XML Preview</CardTitle>
                <CardDescription>
                  First 1000 characters of generated sitemap.xml
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={sitemapXML.substring(0, 1000) + '...'}
                  readOnly
                  className="font-mono text-xs h-96"
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="stats">
            <Card>
              <CardHeader>
                <CardTitle>Detailed Statistics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-muted-foreground">Total URLs</span>
                    <div className="text-2xl font-bold">{stats.totalURLs.toLocaleString()}</div>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">City Landings</span>
                    <div className="text-2xl font-bold">{stats.cityLandings.toLocaleString()}</div>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Specialty Sub-landings</span>
                    <div className="text-2xl font-bold">{stats.specialtyLandings.toLocaleString()}</div>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Static Pages</span>
                    <div className="text-2xl font-bold">{stats.staticPages}</div>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Estimated Indexable</span>
                    <div className="text-2xl font-bold">{stats.estimatedIndexablePages.toLocaleString()}</div>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Total Size</span>
                    <div className="text-2xl font-bold">
                      {(stats.sitemapSize / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <h3 className="font-semibold mb-3">Distribution</h3>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>City Landing Pages</span>
                        <span>{((stats.cityLandings / stats.totalURLs) * 100).toFixed(1)}%</span>
                      </div>
                      <Progress value={(stats.cityLandings / stats.totalURLs) * 100} />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Specialty Sub-landings</span>
                        <span>{((stats.specialtyLandings / stats.totalURLs) * 100).toFixed(1)}%</span>
                      </div>
                      <Progress value={(stats.specialtyLandings / stats.totalURLs) * 100} />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Static & Category Pages</span>
                        <span>{((stats.staticPages / stats.totalURLs) * 100).toFixed(1)}%</span>
                      </div>
                      <Progress value={(stats.staticPages / stats.totalURLs) * 100} />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="structure">
            <Card>
              <CardHeader>
                <CardTitle>URL Structure Examples</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      City Landing Pages
                    </h3>
                    <div className="space-y-1 text-sm font-mono bg-muted p-3 rounded">
                      <div>/city/new-york</div>
                      <div>/city/london</div>
                      <div>/city/tokyo</div>
                      <div>/city/paris</div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <Database className="h-4 w-4" />
                      Specialty Sub-landings
                    </h3>
                    <div className="space-y-1 text-sm font-mono bg-muted p-3 rounded">
                      <div>/city/new-york/anxiety-therapy</div>
                      <div>/city/new-york/depression-counseling</div>
                      <div>/city/london/couples-therapy</div>
                      <div>/city/tokyo/child-psychologist</div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-2">Priority Distribution</h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge>1.0</Badge>
                        <span className="text-sm">Homepage</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">0.9</Badge>
                        <span className="text-sm">Large cities (&gt;1M population)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">0.8</Badge>
                        <span className="text-sm">Medium cities (500K-1M)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">0.7</Badge>
                        <span className="text-sm">Small cities (&lt;500K)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">0.6</Badge>
                        <span className="text-sm">Specialty sub-landings</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default SitemapGeneratorDashboard;
